/**
 * @engine/vfx - Generic Attribute Modifier Nodes
 *
 * Composable system for modifying particle attributes with different:
 * - Composition modes: Set/Overwrite, Multiply, Blend, Add
 * - Attributes: position, velocity, color, alpha, size, angle, etc.
 * - Sample modes: Over Life, By Speed, Random
 *
 * This generates many node variants programmatically.
 */

import type { VFXNodeDefinition, VFXDataType } from '../types';
import { dataPort, flowPort } from '../types';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * How to combine the modifier value with the existing attribute.
 */
export const COMPOSITION_MODES = {
  Set: 0,       // Overwrite: attr = value
  Multiply: 1,  // attr *= value
  Blend: 2,     // attr = lerp(attr, value, blend)
  Add: 3,       // attr += value
} as const;

export type CompositionMode = keyof typeof COMPOSITION_MODES;

/**
 * Particle attributes that can be modified.
 */
export const MODIFIABLE_ATTRIBUTES = {
  // Vectors
  position: { type: 'vec3' as VFXDataType, field: 'particle.position' },
  velocity: { type: 'vec3' as VFXDataType, field: 'particle.velocity' },
  
  // Colors
  color: { type: 'color' as VFXDataType, field: 'particle.color' },
  alpha: { type: 'float' as VFXDataType, field: 'particle.color.a' },
  
  // Scalars
  size: { type: 'float' as VFXDataType, field: 'particle.size' },
  angle: { type: 'float' as VFXDataType, field: 'particle.rotation' },
  angularVelocity: { type: 'float' as VFXDataType, field: 'particle.angularVelocity' },
  
  // Lifecycle (read-only in most cases, but can be modified)
  lifetime: { type: 'float' as VFXDataType, field: 'particle.maxLifetime' },
} as const;

export type ModifiableAttribute = keyof typeof MODIFIABLE_ATTRIBUTES;

/**
 * How to sample the modifier value.
 */
export const SAMPLE_MODES = {
  OverLife: 'overLife',   // Sample based on lifetime/maxLifetime (0-1)
  BySpeed: 'bySpeed',     // Sample based on velocity magnitude
  Random: 'random',       // Random per frame or per particle
  Constant: 'constant',   // Just use the input value directly
} as const;

export type SampleMode = keyof typeof SAMPLE_MODES;

// ============================================================================
// SHADER CODE GENERATION
// ============================================================================

/**
 * Generate the sampling code for a value.
 */
function generateSampleCode(sampleMode: SampleMode, valueType: VFXDataType): string {
  switch (sampleMode) {
    case 'OverLife':
      return `
      let t = particle.lifetime / particle.maxLifetime;
      let sampled = lerp(from, to, t);`;

    case 'BySpeed':
      return `
      let speed = length(particle.velocity);
      let t = clamp(speed / maxSpeed, 0.0, 1.0);
      let sampled = lerp(from, to, t);`;

    case 'Random':
      return valueType === 'vec3' || valueType === 'color'
        ? `let sampled = lerp(from, to, hash3(particle.seed + randomSeed));`
        : `let sampled = lerp(from, to, hash(particle.seed + randomSeed));`;

    case 'Constant':
      return `let sampled = value;`;

    default:
      return `let sampled = from;`;
  }
}

/**
 * Generate the composition code to apply sampled value to attribute.
 */
function generateCompositionCode(
  composition: CompositionMode, 
  field: string, 
  valueType: VFXDataType
): string {
  switch (composition) {
    case 'Set':
      return `${field} = sampled;`;

    case 'Multiply':
      return `${field} *= sampled;`;

    case 'Add':
      return `${field} += sampled;`;

    case 'Blend':
      return `${field} = lerp(${field}, sampled, blend);`;

    default:
      return `${field} = sampled;`;
  }
}

// ============================================================================
// NODE DEFINITIONS
// ============================================================================

/**
 * Create a generic attribute modifier node definition.
 */
function createAttributeModifierNode(
  attribute: ModifiableAttribute,
  composition: CompositionMode,
  sampleMode: SampleMode
): VFXNodeDefinition {
  const attrDef = MODIFIABLE_ATTRIBUTES[attribute];
  const valueType = attrDef.type;
  const field = attrDef.field;
  
  // Build node ID
  const compStr = composition.toLowerCase();
  const sampleStr = sampleMode === 'OverLife' ? 'over_life' 
    : sampleMode === 'BySpeed' ? 'by_speed' 
    : sampleMode === 'Random' ? 'random'
    : 'constant';
  const id = `vfx.update.${compStr}_${attribute}_${sampleStr}`;
  
  // Build human-readable name
  const compName = composition === 'Set' ? 'Set' : composition;
  const sampleName = sampleMode === 'OverLife' ? 'Over Life'
    : sampleMode === 'BySpeed' ? 'By Speed'
    : sampleMode === 'Random' ? 'Random'
    : '';
  const attrName = attribute.charAt(0).toUpperCase() + attribute.slice(1);
  const name = sampleName ? `${compName} ${attrName} ${sampleName}` : `${compName} ${attrName}`;
  
  // Build inputs based on sample mode and composition
  const inputs: ReturnType<typeof dataPort>[] = [
    flowPort('exec', ''),
  ];
  
  // Add value inputs based on sample mode
  if (sampleMode === 'OverLife' || sampleMode === 'BySpeed' || sampleMode === 'Random') {
    inputs.push(dataPort('from', 'From', valueType, { defaultValue: getDefaultValue(valueType, 1) }));
    inputs.push(dataPort('to', 'To', valueType, { defaultValue: getDefaultValue(valueType, 0) }));
  } else {
    inputs.push(dataPort('value', 'Value', valueType, { defaultValue: getDefaultValue(valueType, 1) }));
  }
  
  // By Speed needs max speed
  if (sampleMode === 'BySpeed') {
    inputs.push(dataPort('maxSpeed', 'Max Speed', 'float', { defaultValue: 100, range: { min: 1, max: 1000 } }));
  }
  
  // Random needs seed offset
  if (sampleMode === 'Random') {
    inputs.push(dataPort('randomSeed', 'Seed Offset', 'uint', { defaultValue: 0 }));
  }
  
  // Blend needs blend factor
  if (composition === 'Blend') {
    inputs.push(dataPort('blend', 'Blend', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }));
  }
  
  // Generate shader body (real Slang function body — port ids are plain identifiers)
  const sampleCode = generateSampleCode(sampleMode, valueType);
  const compositionCode = generateCompositionCode(composition, field, valueType);
  const code = sampleCode + '\n    ' + compositionCode;

  return {
    id,
    name,
    category: 'attribute',
    contexts: ['update'],
    description: `${compName} ${attrName.toLowerCase()} ${sampleName.toLowerCase()}`.trim(),
    color: '#FF9800',
    inputs,
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: { code },
  };
}

/**
 * Get default value for a data type.
 */
function getDefaultValue(type: VFXDataType, scalar: number): unknown {
  switch (type) {
    case 'vec3':
      return { x: scalar, y: scalar, z: scalar };
    case 'color':
      return { r: scalar, g: scalar, b: scalar, a: 1 };
    case 'float':
    case 'int':
    case 'uint':
      return scalar;
    default:
      return scalar;
  }
}

// ============================================================================
// GENERATE COMMON MODIFIERS
// ============================================================================

/**
 * Generate commonly used attribute modifier nodes.
 * 
 * We don't generate ALL combinations (would be 4×8×4 = 128 nodes!),
 * just the most useful ones.
 */
export function generateAttributeModifierNodes(): VFXNodeDefinition[] {
  const nodes: VFXNodeDefinition[] = [];
  
  // Size modifiers (most common)
  nodes.push(createAttributeModifierNode('size', 'Multiply', 'OverLife'));
  nodes.push(createAttributeModifierNode('size', 'Set', 'OverLife'));
  nodes.push(createAttributeModifierNode('size', 'Multiply', 'BySpeed'));
  nodes.push(createAttributeModifierNode('size', 'Add', 'Random'));
  
  // Alpha modifiers
  nodes.push(createAttributeModifierNode('alpha', 'Multiply', 'OverLife'));
  nodes.push(createAttributeModifierNode('alpha', 'Set', 'OverLife'));
  nodes.push(createAttributeModifierNode('alpha', 'Multiply', 'BySpeed'));
  
  // Color modifiers (without gradient - linear interpolation)
  nodes.push(createAttributeModifierNode('color', 'Set', 'OverLife'));
  nodes.push(createAttributeModifierNode('color', 'Blend', 'OverLife'));
  nodes.push(createAttributeModifierNode('color', 'Set', 'BySpeed'));
  
  // Velocity modifiers
  nodes.push(createAttributeModifierNode('velocity', 'Multiply', 'OverLife'));
  nodes.push(createAttributeModifierNode('velocity', 'Add', 'Random'));
  
  // Angle modifiers
  nodes.push(createAttributeModifierNode('angle', 'Add', 'OverLife'));
  nodes.push(createAttributeModifierNode('angularVelocity', 'Multiply', 'OverLife'));
  
  return nodes;
}

/**
 * Pre-generated common attribute modifier nodes.
 */
export const ATTRIBUTE_MODIFIER_NODES: readonly VFXNodeDefinition[] = generateAttributeModifierNodes();

// ============================================================================
// SIMPLIFIED LINEAR RANGE NODES
// ============================================================================

/**
 * Simple linear interpolation nodes without curves.
 * These are easier to use when you just want "from X to Y over lifetime".
 */
export const SIMPLE_MODIFIER_NODES: readonly VFXNodeDefinition[] = [
  // Multiply Size Over Life (linear from X to Y)
  {
    id: 'vfx.update.multiply_size_linear',
    name: 'Multiply Size (Linear)',
    category: 'attribute',
    contexts: ['update'],
    description: 'Multiply size linearly from start to end over lifetime',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('start', 'Start', 'float', { defaultValue: 1, range: { min: 0, max: 10 } }),
      dataPort('end', 'End', 'float', { defaultValue: 0.2, range: { min: 0, max: 10 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let t = particle.lifetime / particle.maxLifetime;
        let multiplier = lerp(start, end, t);
        particle.size *= multiplier;
      `,
    },
  },
  
  // Fade Alpha Over Life (linear)
  {
    id: 'vfx.update.fade_alpha_linear',
    name: 'Fade Alpha (Linear)',
    category: 'attribute',
    contexts: ['update'],
    description: 'Fade alpha linearly over lifetime',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('start', 'Start', 'float', { defaultValue: 1, range: { min: 0, max: 1 } }),
      dataPort('end', 'End', 'float', { defaultValue: 0, range: { min: 0, max: 1 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let t = particle.lifetime / particle.maxLifetime;
        particle.color.a *= lerp(start, end, t);
      `,
    },
  },

  // Scale Velocity Over Life (useful for deceleration)
  {
    id: 'vfx.update.scale_velocity_linear',
    name: 'Scale Velocity (Linear)',
    category: 'attribute',
    contexts: ['update'],
    description: 'Scale velocity linearly over lifetime',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('start', 'Start', 'float', { defaultValue: 1, range: { min: 0, max: 10 } }),
      dataPort('end', 'End', 'float', { defaultValue: 0.1, range: { min: 0, max: 10 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let t = particle.lifetime / particle.maxLifetime;
        let scale = lerp(start, end, t);
        particle.velocity *= scale;
      `,
    },
  },

  // Rotate Over Life (add rotation over time)
  {
    id: 'vfx.update.rotate_over_life',
    name: 'Rotate Over Life',
    category: 'attribute',
    contexts: ['update'],
    description: 'Add rotation based on lifetime',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('startSpeed', 'Start Speed', 'float', { defaultValue: 0, range: { min: -10, max: 10 } }),
      dataPort('endSpeed', 'End Speed', 'float', { defaultValue: 3.14159, range: { min: -10, max: 10 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let t = particle.lifetime / particle.maxLifetime;
        let rotSpeed = lerp(startSpeed, endSpeed, t);
        particle.rotation += rotSpeed * deltaTime;
      `,
    },
  },

  // Color Lerp Over Life (without gradient, just two colors)
  {
    id: 'vfx.update.color_lerp_linear',
    name: 'Color Lerp (Linear)',
    category: 'attribute',
    contexts: ['update'],
    description: 'Interpolate between two colors over lifetime',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('colorStart', 'Start Color', 'color', { defaultValue: { r: 1, g: 1, b: 1, a: 1 } }),
      dataPort('colorEnd', 'End Color', 'color', { defaultValue: { r: 0, g: 0, b: 0, a: 0 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let t = particle.lifetime / particle.maxLifetime;
        particle.color = lerp(colorStart, colorEnd, t);
      `,
    },
  },

  // Size By Speed
  {
    id: 'vfx.update.size_by_speed',
    name: 'Size By Speed',
    category: 'attribute',
    contexts: ['update'],
    description: 'Modify size based on velocity magnitude',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('minSize', 'Min Size', 'float', { defaultValue: 0.5, range: { min: 0, max: 10 } }),
      dataPort('maxSize', 'Max Size', 'float', { defaultValue: 2.0, range: { min: 0, max: 10 } }),
      dataPort('maxSpeed', 'Max Speed', 'float', { defaultValue: 100, range: { min: 1, max: 1000 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let speed = length(particle.velocity);
        let t = clamp(speed / maxSpeed, 0.0, 1.0);
        particle.size = lerp(minSize, maxSize, t);
      `,
    },
  },

  // Alpha By Speed (for motion blur effect)
  {
    id: 'vfx.update.alpha_by_speed',
    name: 'Alpha By Speed',
    category: 'attribute',
    contexts: ['update'],
    description: 'Modify alpha based on velocity magnitude',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('minAlpha', 'Min Alpha', 'float', { defaultValue: 0.2, range: { min: 0, max: 1 } }),
      dataPort('maxAlpha', 'Max Alpha', 'float', { defaultValue: 1.0, range: { min: 0, max: 1 } }),
      dataPort('maxSpeed', 'Max Speed', 'float', { defaultValue: 100, range: { min: 1, max: 1000 } }),
    ],
    outputs: [flowPort('next', '')],
    modifiesParticle: true,
    shader: {
      code: `
        let speed = length(particle.velocity);
        let t = clamp(speed / maxSpeed, 0.0, 1.0);
        particle.color.a = lerp(minAlpha, maxAlpha, t);
      `,
    },
  },
];

/**
 * All attribute modifier nodes combined.
 */
export const ALL_ATTRIBUTE_NODES: readonly VFXNodeDefinition[] = [
  ...ATTRIBUTE_MODIFIER_NODES,
  ...SIMPLE_MODIFIER_NODES,
];
